import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Search, Download, ChevronDown, Loader2, AlertCircle, Stethoscope, GraduationCap, Globe, MapPin, Lock, Sparkles, BellRing, Settings, FileSpreadsheet, Upload } from "lucide-react";
import html2pdf from "html2pdf.js";
import api from "@/lib/api";
import { usePublicAuth } from "@/contexts/PublicAuthContext";
import { usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import TeaserAuthModal from "@/components/TeaserAuthModal";
import { toast } from "sonner";

/* ─── colour constants for chance badges ─── */
const CHANCE_COLORS = {
  "Safe Zone":     { bg: "rgba(56,189,248,0.15)", border: "rgba(56,189,248,0.4)", text: "#38bdf8" },
  "High Chance":   { bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.4)", text: "#10b981" },
  "Good Chance":   { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)", text: "#3b82f6" },
  "Worth Trying":  { bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.35)", text: "#a78bfa" },
  "Risky Choice":  { bg: "rgba(234,179,8,0.12)", border: "rgba(234,179,8,0.35)", text: "#eab308" },
  "Slight Risk":   { bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.35)", text: "#f97316" },
  "Low Chance":    { bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.35)", text: "#f97316" },
  "Dream College": { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.30)", text: "#ef4444" },
};

const STREAM_OPTIONS = [
  { value: "STATE_QUOTA", label: "State Quota (Maharashtra 85%)", icon: <MapPin size={16} /> },
  { value: "AIIMS", label: "AIIMS (All India)", icon: <Stethoscope size={16} /> },
  { value: "AIQ_15", label: "AIQ 15% (All India)", icon: <Stethoscope size={16} /> },
  { value: "DEEMED", label: "Deemed Universities", icon: <GraduationCap size={16} /> },
  { value: "OTHER_STATES", label: "Other States Quota", icon: <Globe size={16} /> },
];

const COURSE_MAP = {
  STATE_QUOTA: ["MBBS", "BDS", "BAMS", "BHMS", "BPTH"],
  AIIMS: ["MBBS"],
  AIQ_15: ["MBBS"],
  DEEMED: ["MBBS", "BDS", "BAMS"],
  OTHER_STATES: ["MBBS"],
};

const STATE_OPTIONS = [
  { value: "TELANGANA", label: "Telangana" },
  { value: "ANDRA_PRADESH", label: "Andhra Pradesh" },
  { value: "UP", label: "Uttar Pradesh" },
  { value: "KARNATAKA", label: "Karnataka" },
];

const MAHA_CATEGORIES = ["OPEN", "OBC", "SC", "ST", "EWS", "VJA", "NTB", "NTC", "NTD", "SEBC"];
const AIIMS_CATEGORIES = ["OPEN", "OBC", "EWS", "SC", "ST", "OPEN PWD", "OBC PWD", "EWS PWD", "SC PWD", "ST PWD"];

const SUMMARY_KEYS = [
  { key: "almost_certain", label: "Safe Zone", color: "#38bdf8" },
  { key: "very_likely", label: "High Chance", color: "#10b981" },
  { key: "likely", label: "Good Chance", color: "#3b82f6" },
  { key: "possible", label: "Worth Trying", color: "#a78bfa" },
  { key: "borderline", label: "Risky Choice", color: "#eab308" },
  { key: "slight_risk", label: "Slight Risk", color: "#f97316" },
  { key: "unlikely", label: "Low Chance", color: "#f97316" },
  { key: "very_unlikely", label: "Dream College", color: "#ef4444" },
];

/* ─── reusable styled select ─── */
const StyledSelect = ({ label, icon, value, onChange, children, id }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label htmlFor={id} style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#94a3b8", display: "flex", alignItems: "center", gap: 5 }}>
      {icon}{label}
    </label>
    <select id={id} value={value} onChange={onChange} style={{ background: "rgba(0,0,0,0.4)", padding: "0.55rem 0.7rem", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", outline: "none", fontSize: "0.82rem", fontFamily: "inherit", cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", paddingRight: 30, width: "100%" }}>
      {children}
    </select>
  </div>
);

/* ─── reusable styled number input ─── */
const StyledInput = ({ label, icon, value, onChange, placeholder, id, required }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label htmlFor={id} style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#94a3b8", display: "flex", alignItems: "center", gap: 5 }}>
      {icon}{label}{required && <span style={{ color: "#f43f5e" }}>*</span>}
    </label>
    <input id={id} type="number" value={value} onChange={onChange} placeholder={placeholder} style={{ background: "rgba(0,0,0,0.4)", padding: "0.55rem 0.7rem", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", outline: "none", fontSize: "0.82rem", fontFamily: "inherit", width: "100%" }} />
  </div>
);


/* ─── format fees helper ─── */
const formatFees = (fees) => {
  if (!fees || fees === "—" || fees === "N/A") return "—";
  let numStr = String(fees).replace(/,/g, '').trim();
  const num = parseFloat(numStr);
  if (!isNaN(num)) {
    return "₹ " + num.toLocaleString('en-IN');
  }
  if (String(fees).includes('₹')) return fees;
  return "₹ " + fees;
};

export default function PublicPredictor() {
  const { user } = usePublicAuth();
  const { admin: currentAdmin, token } = usePublicAdmin();
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [autoTrigger, setAutoTrigger] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  // --- Start Suggestion List modal state ---
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggModalLoading, setSuggModalLoading] = useState(false);
  const [suggRegNumber, setSuggRegNumber] = useState("");
  const [suggStudentName, setSuggStudentName] = useState("");
  const [suggAir, setSuggAir] = useState("");
  const [suggSml, setSuggSml] = useState("");
  const [suggCatRank, setSuggCatRank] = useState("");

  const PredictorSwitch = ({ label, description, checked, onChange }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginRight: 12 }}>
        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#f8fafc" }}>{label}</span>
        {description && <span style={{ fontSize: "0.68rem", color: "#64748b" }}>{description}</span>}
      </div>
      <div 
        onClick={() => onChange(!checked)} 
        style={{
          width: 32,
          height: 16,
          background: checked ? "#f39c12" : "#334155",
          borderRadius: 999,
          padding: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: checked ? "flex-end" : "flex-start",
          transition: "all 0.2s"
        }}
      >
        <div style={{ width: 12, height: 12, background: "#fff", borderRadius: "50%" }} />
      </div>
    </div>
  );

  const isAdminPredictor = typeof window !== "undefined" && window.location.pathname.startsWith("/portal-admin");
  const isMasterAdmin = isAdminPredictor && currentAdmin?.role === "PUBLIC_ADMIN";
  const isGuestRoute = typeof window !== "undefined" && window.location.pathname.includes("guest");
  const isGuest = (!user && !isAdminPredictor) || isGuestRoute;

  const updateAdminSetting = (key, value) => {
    if (isAdminPredictor) {
      try {
        const saved = localStorage.getItem("ame_admin_predictor_settings");
        const current = saved ? JSON.parse(saved) : {};
        current[key] = value;
        localStorage.setItem("ame_admin_predictor_settings", JSON.stringify(current));
      } catch (e) {
        console.error("Failed to save admin setting", e);
      }
    }
  };

  console.log("DEBUG - TeaserAuthModal is:", TeaserAuthModal);

  /* ─── visibility configuration state ─── */
  const [isPredictorEnabled, setIsPredictorEnabled] = useState(null); // null means loading
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [allowedStreams, setAllowedStreams] = useState({
    STATE_QUOTA: true,
    AIIMS: true,
    AIQ_15: true,
    DEEMED: true,
    OTHER_STATES: true
  });
  const [showSml, setShowSml] = useState(true);
  const [showCatRank, setShowCatRank] = useState(true);

  const isSettingAllowed = (settingId) => {
    if (!isAdminPredictor) return false;
    if (currentAdmin?.role === "PUBLIC_ADMIN") return true;
    if (currentAdmin?.role === "PUBLIC_SUBADMIN") {
      // By default if missing (old data), allow all
      if (!currentAdmin.allowed_predictor_settings) return true;
      return currentAdmin.allowed_predictor_settings.includes(settingId);
    }
    return false;
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get("/public/settings");
        let initialPredictorEnabled = !!res.data?.show_predictor;
        
        const stateQuota = res.data?.show_state_quota !== false;
        const aiims = res.data?.show_aiims !== false;
        const aiq15 = res.data?.show_aiq_15 !== false;
        const deemed = res.data?.show_deemed !== false;
        const otherStates = res.data?.show_other_states !== false;
        
        let initialAllowedStreams = {
          STATE_QUOTA: stateQuota,
          AIIMS: aiims,
          AIQ_15: aiq15,
          DEEMED: deemed,
          OTHER_STATES: otherStates
        };

        let initialShowSml = res.data?.predictor_show_sml !== false;
        let initialShowCatRank = res.data?.predictor_show_cat_rank !== false;
        let initialColShowCategory = res.data?.col_show_category !== false;
        let initialColShowCutoff = res.data?.col_show_cutoff !== false;
        let initialColShowYourRank = res.data?.col_show_your_rank !== false;
        let initialColShowMargin = res.data?.col_show_margin !== false;
        let initialColShowBuffer = res.data?.col_show_buffer !== false;
        let initialAllowPdfDownload = res.data?.allow_pdf_download !== false;
        let initialColShowChance = res.data?.col_show_chance !== false;

        if (isAdminPredictor) {
          if (currentAdmin?.role === "PUBLIC_SUBADMIN") {
            const allowed = currentAdmin.allowed_predictor_settings;
            if (allowed) {
              if (!allowed.includes("isPredictorEnabled")) initialPredictorEnabled = false;
              if (!allowed.includes("allowedStreams_STATE_QUOTA")) initialAllowedStreams.STATE_QUOTA = false;
              if (!allowed.includes("allowedStreams_AIIMS")) initialAllowedStreams.AIIMS = false;
              if (!allowed.includes("allowedStreams_AIQ_15")) initialAllowedStreams.AIQ_15 = false;
              if (!allowed.includes("allowedStreams_DEEMED")) initialAllowedStreams.DEEMED = false;
              if (!allowed.includes("allowedStreams_OTHER_STATES")) initialAllowedStreams.OTHER_STATES = false;
              if (!allowed.includes("showSml")) initialShowSml = false;
              if (!allowed.includes("showCatRank")) initialShowCatRank = false;
              if (!allowed.includes("colShowCategory")) initialColShowCategory = false;
              if (!allowed.includes("colShowCutoff")) initialColShowCutoff = false;
              if (!allowed.includes("colShowYourRank")) initialColShowYourRank = false;
              if (!allowed.includes("colShowMargin")) initialColShowMargin = false;
              if (!allowed.includes("colShowBuffer")) initialColShowBuffer = false;
              if (!allowed.includes("allowPdfDownload")) initialAllowPdfDownload = false;
              if (!allowed.includes("colShowChance")) initialColShowChance = false;
              if (!allowed.includes("colShowFees")) setColShowFees(false);
              if (!allowed.includes("showRoundWiseCutoff")) setShowRoundWiseCutoff(false);
              if (!allowed.includes("includeRoundsInPdf")) setIncludeRoundsInPdf(false);
            }
          } else {
            try {
              const saved = localStorage.getItem("ame_admin_predictor_settings");
              if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.isPredictorEnabled !== undefined) initialPredictorEnabled = parsed.isPredictorEnabled;
                if (parsed.allowedStreams !== undefined) initialAllowedStreams = parsed.allowedStreams;
                if (parsed.showSml !== undefined) initialShowSml = parsed.showSml;
                if (parsed.showCatRank !== undefined) initialShowCatRank = parsed.showCatRank;
                if (parsed.colShowCategory !== undefined) initialColShowCategory = parsed.colShowCategory;
                if (parsed.colShowCutoff !== undefined) initialColShowCutoff = parsed.colShowCutoff;
                if (parsed.colShowYourRank !== undefined) initialColShowYourRank = parsed.colShowYourRank;
                if (parsed.colShowMargin !== undefined) initialColShowMargin = parsed.colShowMargin;
                if (parsed.colShowBuffer !== undefined) initialColShowBuffer = parsed.colShowBuffer;
                if (parsed.allowPdfDownload !== undefined) initialAllowPdfDownload = parsed.allowPdfDownload;
                if (parsed.colShowChance !== undefined) initialColShowChance = parsed.colShowChance;
              }
            } catch (e) {
              console.error("Failed to parse saved admin settings", e);
            }
          }
        }

        setIsPredictorEnabled(initialPredictorEnabled);
        setAllowedStreams(initialAllowedStreams);
        setShowSml(initialShowSml);
        setShowCatRank(initialShowCatRank);
        setColShowCategory(initialColShowCategory);
        setColShowCutoff(initialColShowCutoff);
        setColShowYourRank(initialColShowYourRank);
        setColShowMargin(initialColShowMargin);
        setColShowBuffer(initialColShowBuffer);
        setAllowPdfDownload(initialAllowPdfDownload);
        setColShowChance(initialColShowChance);

        if (!initialAllowedStreams.STATE_QUOTA) {
          if (initialAllowedStreams.AIIMS) setStream("AIIMS");
          else if (initialAllowedStreams.DEEMED) setStream("DEEMED");
          else if (initialAllowedStreams.OTHER_STATES) setStream("OTHER_STATES");
        }
      } catch (err) {
        console.error("Failed to load predictor settings", err);
        setIsPredictorEnabled(false);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  /* ─── column visibility states ─── */
  const [colShowCategory, setColShowCategory] = useState(true);
  const [colShowCutoff, setColShowCutoff] = useState(true);
  const [colShowYourRank, setColShowYourRank] = useState(true);
  const [colShowMargin, setColShowMargin] = useState(true);
  const [colShowBuffer, setColShowBuffer] = useState(true);
  const [allowPdfDownload, setAllowPdfDownload] = useState(true);
  const [colShowChance, setColShowChance] = useState(true);
  const [colShowFees, setColShowFees] = useState(true);

  /* ─── round-wise cutoff display states ─── */
  const [showRoundWiseCutoff, setShowRoundWiseCutoff] = useState(false);
  const [selectedRound, setSelectedRound] = useState("ALL"); // "ALL"|"R1"|"R2"|"R3"|"R4"
  const [includeRoundsInPdf, setIncludeRoundsInPdf] = useState(false);
  /* ─── form state ─── */
  const [stream, setStream] = useState("STATE_QUOTA");
  const [course, setCourse] = useState("MBBS");
  const [state, setState] = useState("TELANGANA");
  const [category, setCategory] = useState("OPEN");
  const [gender, setGender] = useState("Male");
  const [air, setAir] = useState("");
  const [sml, setSml] = useState("");
  const [catRank, setCatRank] = useState("");
  // Prediction mode: "AIR" | "SML" | "CAT_RANK" | "ALL"
  const [predictionMode, setPredictionMode] = useState("ALL");
  // Prediction scope: "ALL" | "R1"
  const [predictionScope, setPredictionScope] = useState("ALL");

  /* ─── results state ─── */
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [pdfStudentName, setPdfStudentName] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  /* ─── bulk upload state ─── */
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkStudents, setBulkStudents] = useState([]); // processed student results
  const [bulkExpandedIdx, setBulkExpandedIdx] = useState(null); // which student is expanded

  /* reset course when stream changes */
  const handleStreamChange = (val) => {
    setStream(val);
    const courses = COURSE_MAP[val] || [];
    if (!courses.includes(course)) setCourse(courses[0] || "MBBS");
    
    // Reset category if not valid for the new stream
    const cats = (val === "AIIMS" || val === "AIQ_15") ? AIIMS_CATEGORIES : MAHA_CATEGORIES;
    if (!cats.includes(category)) setCategory("OPEN");

    setPredictions([]); setSummary(null); setHasSearched(false); setError("");
  };


  const needsCategory = stream === "STATE_QUOTA" || stream === "AIIMS" || stream === "AIQ_15";
  const needsGender = stream === "STATE_QUOTA";
  // Show SML input for STATE_QUOTA if mode requires it, OR if category is EWS (private colleges require SML for OPEN quota)
  const needsSml = stream === "STATE_QUOTA" && showSml && !isGuest && (predictionMode === "SML" || predictionMode === "ALL" || (category === "EWS" && predictionMode === "CAT_RANK"));
  // Show Cat-Rank input only for STATE_QUOTA, reserved category, if mode requires it, and not a guest
  const needsCatRank = stream === "STATE_QUOTA" && category !== "OPEN" && showCatRank && !isGuest && (predictionMode === "CAT_RANK" || predictionMode === "ALL");
  // Show AIR input if mode requires it
  const needsAir = predictionMode === "AIR" || predictionMode === "ALL" || stream !== "STATE_QUOTA";
  // Whether prediction mode selector is relevant (only for STATE_QUOTA)
  const showModeSelector = stream === "STATE_QUOTA" && !isGuest;

  /* ─── predict ─── */
  const handlePredict = useCallback(async () => {
    setError("");

    // Validate based on prediction mode
    if (stream === "STATE_QUOTA" && !isGuest) {
      if (predictionMode === "AIR" && !air) {
        setError("Please enter your All India Rank (AIR).");
        return;
      }
      if (predictionMode === "SML" && !sml) {
        setError("Please enter your State Merit List (SML) Rank.");
        return;
      }
      if (predictionMode === "CAT_RANK") {
        if (!catRank) {
          setError("Please enter your Category Rank.");
          return;
        }
        if (category === "EWS" && !sml) {
          setError("Please enter your State Merit List (SML) rank as well. Private colleges do not have EWS quota and are evaluated under OPEN quota with 50% fees concession.");
          return;
        }
      }
      if (predictionMode === "ALL" && !air && !sml && !catRank) {
        setError("Please enter at least one rank: AIR, SML, or Category Rank.");
        return;
      }
    } else {
      if (!air) {
        setError("Please enter your All India Rank (AIR).");
        return;
      }
    }

    setLoading(true);
    setPredictions([]); setSummary(null); setHasSearched(true);
    try {
      // Build payload — send the ranks relevant to the selected mode and CTP
      const isStateQuota = stream === "STATE_QUOTA";
      const sendAir = !isStateQuota || predictionMode === "AIR" || predictionMode === "ALL" || (Boolean(air) && predictionMode === "CAT_RANK");
      const sendSml = isStateQuota && (predictionMode === "SML" || predictionMode === "ALL" || (category === "EWS" && Boolean(sml)) || Boolean(sml));
      const sendCatRank = isStateQuota && (predictionMode === "CAT_RANK" || predictionMode === "ALL" || Boolean(catRank));

      const body = {
        stream,
        course,
        state: stream === "OTHER_STATES" ? state : null,
        category: needsCategory ? category : "OPEN",
        gender: needsCategory ? gender : "Male",
        air: sendAir && air ? parseInt(air, 10) : null,
        sml: sendSml && sml ? parseInt(sml, 10) : null,
        category_rank: sendCatRank && catRank ? parseInt(catRank, 10) : null,
        target_round: predictionScope,
        prediction_mode: predictionMode,
        include_air: predictionMode === "AIR" || predictionMode === "ALL"
      };
      const res = await api.post("/medical-predictor/predict", body);
      setPredictions(res.data.predictions || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Prediction failed. Please try again.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [stream, course, state, category, gender, air, sml, catRank, needsCategory, predictionMode, predictionScope, isGuest]);

  useEffect(() => {
    if (user) {
      const savedAir = localStorage.getItem("guest_pred_air");
      const savedCategory = localStorage.getItem("guest_pred_category");
      const savedGender = localStorage.getItem("guest_pred_gender");
      const savedStream = localStorage.getItem("guest_pred_stream");
      const savedCourse = localStorage.getItem("guest_pred_course");

      if (savedAir) {
        setAir(savedAir);
        if (savedCategory) setCategory(savedCategory);
        if (savedGender) setGender(savedGender);
        if (savedStream) setStream(savedStream);
        if (savedCourse) setCourse(savedCourse);

        localStorage.removeItem("guest_pred_air");
        localStorage.removeItem("guest_pred_category");
        localStorage.removeItem("guest_pred_gender");
        localStorage.removeItem("guest_pred_stream");
        localStorage.removeItem("guest_pred_course");

        setAutoTrigger(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (autoTrigger && air) {
      setAutoTrigger(false);
      handlePredict();
    }
  }, [autoTrigger, air]); // eslint-disable-line

  /* ─── filtered results ─── */
  const baseFiltered = searchFilter
    ? predictions.filter(p => (p.college_name || "").toLowerCase().includes(searchFilter.toLowerCase()) || (p.state || "").toLowerCase().includes(searchFilter.toLowerCase()) || (p.college_code || "").toLowerCase().includes(searchFilter.toLowerCase()))
    : predictions;

  const actuallyShowFees = colShowFees && !["BHMS", "BPTH"].includes((course || "").toUpperCase());

  const filtered = !isGuest
    ? [...baseFiltered].sort((a, b) => {
        if (a.priority_order != null && b.priority_order != null && a.priority_order !== 999 && b.priority_order !== 999) {
          return a.priority_order - b.priority_order;
        }
        if (a.priority_order != null && a.priority_order !== 999) return -1;
        if (b.priority_order != null && b.priority_order !== 999) return 1;

        const cutA = a.cutoff_rank != null ? a.cutoff_rank : 9999999;
        const cutB = b.cutoff_rank != null ? b.cutoff_rank : 9999999;
        return cutA - cutB;
      })
    : baseFiltered;

  const handleDownloadPDF = () => {
    if (!predictions || predictions.length === 0) return;

    let studentName = "";
    if (isAdminPredictor) {
      studentName = pdfStudentName.trim() || "Student";
    } else {
      studentName = user?.full_name || user?.name || "Guest Candidate";
    }
    const dateStr = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });

    const streamLabel = STREAM_OPTIONS.find(o => o.value === stream)?.label || stream;
    
    // Headers list based on visibility settings
    const headers = [
      { label: "College", show: true, width: "30%" },
      colShowCategory ? { label: "Category", show: true, width: "10%" } : null,
      actuallyShowFees ? { label: "Fees", show: true, width: "8%" } : null,
      ...(includeRoundsInPdf && showRoundWiseCutoff ? roundCols.map(r => ({ label: r, show: true, width: "8%" })) : []),
      colShowCutoff ? { label: "Cutoff", show: true, width: "8%" } : null,
      colShowYourRank ? { label: "Your Rank", show: true, width: "8%" } : null,
      colShowBuffer ? { label: "Buffer", show: true, width: "9%" } : null,
      colShowMargin ? { label: "Margin", show: true, width: "8%" } : null,
      predictionScope === "ALL" ? { label: "Exp. Round", show: true, width: "12%" } : null,
      colShowChance ? { label: "Chance", show: true, width: "10%" } : null
    ].filter(Boolean);

    // Formatted rows
    const rowsHtml = filtered.map((p, i) => {
      const cc = CHANCE_COLORS[p.chance_label] || { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.1)", text: "#94a3b8" };
      const buffer = Math.round(p.student_rank - p.cutoff_rank);
      const bufferText = buffer <= 0 ? `-${Math.abs(buffer).toLocaleString()}` : `+${buffer.toLocaleString()}`;
      const bufferColor = buffer <= 0 ? "#10b981" : "#f43f5e";
      const rowLeftBorder = colShowChance ? `3px solid ${cc.text}` : "3px solid transparent";

      return `
        <tr style="page-break-inside: avoid;">
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; border-left: ${rowLeftBorder}; font-weight: 600; color: #1e293b;">
            <div style="font-size: 11.5px; line-height: 1.3;">${p.college_name}</div>
            <div style="font-size: 9px; color: #64748b; margin-top: 2px;">${[p.college_code, p.status, p.state].filter(Boolean).join(" · ")}</div>
          </td>
          ${colShowCategory ? `
            <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #334155; vertical-align: middle;">
              <div style="font-weight: 500;">${p.matched_category}</div>
              <div style="font-size: 9.5px; color: #64748b;">${p.matched_rank_type}</div>
            </td>
          ` : ""}
          ${actuallyShowFees ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #10b981; font-weight: 600; vertical-align: middle;">${formatFees(p.fees)}</td>` : ""}
          ${includeRoundsInPdf && showRoundWiseCutoff ? roundCols.map(rnd => {
            const airVal = p[`${rnd.toLowerCase()}_air`];
            const scoreVal = p[`${rnd.toLowerCase()}_score`];
            const smlVal = p[`${rnd.toLowerCase()}_sml`];
            const catVal = p[`${rnd.toLowerCase()}_cat_rank`];
            
            // Determine which values to show based on prediction mode
            const showAir = predictionMode === "AIR" || predictionMode === "ALL" || stream !== "STATE_QUOTA";
            const showSml = stream === "STATE_QUOTA" && (predictionMode === "SML" || predictionMode === "ALL");
            const showCat = stream === "STATE_QUOTA" && (predictionMode === "CAT_RANK" || predictionMode === "ALL");
            
            const hasAny = (showAir && (airVal != null || scoreVal != null)) || (showSml && smlVal != null) || (showCat && catVal != null);

            return `
              <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: middle;">
                ${hasAny ? `
                  <div style="display: flex; flex-direction: column; gap: 4px;">
                    ${showAir && airVal != null ? `
                      <div>
                        <div style="font-size: 11px; color: #0284c7; font-weight: 600;">${Math.round(airVal).toLocaleString()}</div>
                        ${predictionMode === "ALL" ? `<div style="font-size: 8px; color: #64748b; text-transform: uppercase;">AIR</div>` : ""}
                        ${scoreVal != null ? `<div style="font-size: 9px; color: #64748b; margin-top: 1px;">Score: ${Math.round(scoreVal)}</div>` : ""}
                      </div>
                    ` : ""}
                    ${showSml && smlVal != null ? `
                      <div>
                        <div style="font-size: 11px; color: #8b5cf6; font-weight: 600;">${Math.round(smlVal).toLocaleString()}</div>
                        ${predictionMode === "ALL" ? `<div style="font-size: 8px; color: #64748b; text-transform: uppercase;">SML</div>` : ""}
                      </div>
                    ` : ""}
                    ${showCat && catVal != null ? `
                      <div>
                        <div style="font-size: 11px; color: #10b981; font-weight: 600;">${Math.round(catVal).toLocaleString()}</div>
                        ${predictionMode === "ALL" ? `<div style="font-size: 8px; color: #64748b; text-transform: uppercase;">CAT-RANK</div>` : ""}
                      </div>
                    ` : ""}
                  </div>
                ` : `<span style="font-size: 10px; color: #94a3b8;">—</span>`}
              </td>
            `;
          }).join("") : ""}
          ${colShowCutoff ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #475569; vertical-align: middle;">${Math.round(p.cutoff_rank).toLocaleString()}</td>` : ""}
          ${colShowYourRank ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #1e293b; font-weight: 600; vertical-align: middle;">${Math.round(p.student_rank).toLocaleString()}</td>` : ""}
          ${colShowBuffer ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: ${bufferColor}; font-weight: 600; vertical-align: middle;">${bufferText}</td>` : ""}
          ${colShowMargin ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: ${cc.text}; font-weight: 700; vertical-align: middle;">${p.margin_percent != null ? (p.margin_percent > 0 ? "+" : "") + p.margin_percent.toFixed(1) + "%" : "--"}</td>` : ""}
          ${predictionScope === "ALL" ? `
            <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 10px; vertical-align: middle;">
              ${(p.expected_rounds || []).map((er, idx) => {
                 const bg = CHANCE_COLORS[er.chance]?.bg || '#f1f5f9';
                 const border = CHANCE_COLORS[er.chance]?.border || '#cbd5e1';
                 const text = CHANCE_COLORS[er.chance]?.text || '#475569';
                 let t = '<span style="background: ' + bg + '; border: 1px solid ' + border + '; color: ' + text + '; padding: 2px 4px; border-radius: 4px; white-space: nowrap; font-weight: 700;">' + er.round + '</span>';
                 if (idx > 0) t = '<span style="color: #94a3b8; font-size: 8px; margin: 0 2px;">➔</span>' + t;
                 return t;
              }).join("") || "—"}
            </td>
          ` : ""}
          ${colShowChance ? `
            <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: middle;">
              <span style="font-size: 9.5px; font-weight: 700; padding: 3px 8px; border-radius: 4px; background: ${cc.bg}; border: 1px solid ${cc.border}; color: ${cc.text}; white-space: nowrap; display: inline-block;">
                ${p.chance_label}
              </span>
            </td>
          ` : ""}
        </tr>
      `;
    }).join("");

    // Formatted summary counts list
    const summaryHtml = SUMMARY_KEYS.map(sk => {
      const val = summary?.[sk.key] || 0;
      if (val === 0) return "";
      return `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 8px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; min-width: 75px; text-align: center;">
          <span style="font-size: 14px; font-weight: 800; color: ${sk.color};">${val}</span>
          <span style="font-size: 9px; font-weight: 600; color: #475569; margin-top: 1px;">${sk.label}</span>
        </div>
      `;
    }).join("");

    // Check for stray vacancy matches
    const hasStrayVacancy = filtered.some(p => p.expected_rounds && p.expected_rounds.some(r => r.round === "R4" || r.round === "R3"));

    const strayWarningHtml = hasStrayVacancy && predictionScope === "ALL" ? `
      <div style="margin-top: 30px; background: #fff1f2; border: 1px solid #fda4af; border-radius: 10px; padding: 15px; page-break-inside: avoid;">
        <div style="font-size: 14px; font-weight: 800; color: #e11d48; margin-bottom: 6px; display: flex; align-items: center; gap: 8px;">
          <span>🔴</span> Counselling Alert: Stray Vacancy
        </div>
        <div style="font-size: 11px; color: #9f1239; line-height: 1.5; font-weight: 500;">
          You have a chance at some colleges only during the Stray Vacancy / Mop-up round. 
          <strong style="font-weight: 800;">CRITICAL RULE:</strong> You are ONLY eligible to participate in Stray Vacancy if you have 
          NOT been allotted or admitted to any seat in previous rounds. Relying on this round is highly unpredictable as seats may not even remain vacant. 
          You must check the official seat matrix and consult your counsellor.
        </div>
      </div>
    ` : "";

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Admissions Report - ${studentName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
          body {
            font-family: 'Outfit', sans-serif;
            padding: 30px;
            color: #1e293b;
            background: #fff;
            margin: 0;
            line-height: 1.4;
          }
          .brand-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #38bdf8;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .brand-title {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
            margin: 0;
          }
          .brand-title span {
            color: #38bdf8;
          }
          .brand-subtitle {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #64748b;
            font-weight: 700;
            margin-top: 2px;
          }
          .doc-type {
            text-align: right;
          }
          .doc-type-badge {
            background: #e0f2fe;
            color: #0369a1;
            padding: 4px 12px;
            border-radius: 50px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            display: inline-block;
          }
          .doc-date {
            font-size: 11px;
            color: #64748b;
            margin-top: 5px;
            font-weight: 500;
          }
          .student-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 15px 20px;
            margin-bottom: 20px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .info-group {
            display: flex;
            flex-direction: column;
          }
          .info-label {
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #64748b;
            font-weight: 700;
            margin-bottom: 3px;
          }
          .info-value {
            font-size: 13px;
            color: #0f172a;
            font-weight: 600;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11.5px;
            margin-top: 10px;
          }
          th {
            background: #0f172a;
            color: #fff;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 9.5px;
            letter-spacing: 0.8px;
            padding: 10px;
            text-align: left;
          }
          th:first-child {
            border-top-left-radius: 6px;
            border-bottom-left-radius: 6px;
          }
          th:last-child {
            border-top-right-radius: 6px;
            border-bottom-right-radius: 6px;
          }
          .print-footer {
            margin-top: 40px;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
            display: flex;
            justify-content: space-between;
            font-size: 9.5px;
            color: #94a3b8;
            font-weight: 500;
          }
          .watermark-container {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 480px;
            opacity: 0.045;
            pointer-events: none;
            z-index: -1;
          }
          .watermark-container img {
            width: 100%;
            height: auto;
          }
          .contact-section {
            margin-top: 25px;
            page-break-inside: avoid;
            border-top: 2px dashed #e2e8f0;
            padding-top: 15px;
          }
          .contact-heading {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #475569;
            font-weight: 700;
            margin-bottom: 10px;
          }
          .contact-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .contact-branch {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 10px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .branch-name {
            font-size: 11px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 5px;
            border-bottom: 1px solid #cbd5e1;
            padding-bottom: 3px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .branch-address {
            font-size: 8.5px;
            color: #64748b;
            line-height: 1.3;
            margin-bottom: 6px;
          }
          .branch-phone-list {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .branch-phone-item {
            font-size: 9.5px;
            font-weight: 600;
            color: #0284c7;
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .counselling-callout {
            margin-top: 12px;
            background: #f0f9ff;
            border: 1px solid #bae6fd;
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 10.5px;
            font-weight: 600;
            color: #0369a1;
            text-align: center;
          }
        </style>
      </head>
      <body>
        
        <div class="watermark-container">
          <img src="/landing-assets/images/branding/ame_logo_watermark.png" alt="AME Watermark" />
        </div>

        <div class="brand-header">
          <div>
            <h1 class="brand-title">Admissions <span>Made Easy</span></h1>
            <div class="brand-subtitle">Smart College Admissions Predictor</div>
          </div>
          <div class="doc-type">
            <div class="doc-type-badge">NEET Prediction Report</div>
            <div class="doc-date">${dateStr}</div>
            ${currentAdmin?.name ? `<div style="font-size: 10px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Counsellor: ${currentAdmin.name}</div>` : ""}
          </div>
        </div>

        <div class="student-card">
          <div class="info-group">
            <div class="info-label">Student Name</div>
            <div class="info-value">${studentName}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Stream / Course</div>
            <div class="info-value">${streamLabel} · ${course}</div>
          </div>
          <div class="info-group">
            <div class="info-label">All India Rank (AIR)</div>
            <div class="info-value">${air ? Math.round(parseInt(air, 10)).toLocaleString() : "-"}</div>
          </div>
          ${sml ? `
            <div class="info-group">
              <div class="info-label">State Merit List (SML)</div>
              <div class="info-value">${Math.round(parseInt(sml, 10)).toLocaleString()}</div>
            </div>
          ` : ""}
          ${catRank ? `
            <div class="info-group">
              <div class="info-label">Category Rank</div>
              <div class="info-value">${Math.round(parseInt(catRank, 10)).toLocaleString()}</div>
            </div>
          ` : ""}
          <div class="info-group">
            <div class="info-label">Reserved Category</div>
            <div class="info-value">${category} (${gender})</div>
          </div>
        </div>

        <div style="margin-bottom: 15px; padding: 10px 15px; background: #fef2f2; border: 1px solid #fca5a5; border-radius: 6px; font-size: 10px; color: #b91c1c; font-weight: 500;">
          🔴 <strong style="color: #991b1b;">Note:</strong> All fee structures are based on the 2025–26 academic year. Fees for 2026–27 are subject to change. Please contact us for updated information.
        </div>

        <div style="margin-bottom: 20px;">
          <h3 style="font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #475569; font-weight: 700; margin-bottom: 8px;">Prediction Analysis Summary</h3>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            ${summaryHtml}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              ${headers.map(h => `<th style="width: ${h.width};">${h.label}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="contact-section">
          <div class="contact-heading">Our Admission Counselling Branches</div>
          <div class="contact-grid">
            
            <!-- Latur -->
            <div class="contact-branch">
              <div>
                <div class="branch-name">Latur Branch (HQ)</div>
                <div class="branch-address">Ground Floor, Vishwa Travels Office, Near Udyog Bhavan, Shivaji Nagar, Latur</div>
              </div>
              <div class="branch-phone-list">
                <div class="branch-phone-item">📞 +91 93095 53235</div>
                <div class="branch-phone-item">📞 +91 99708 09003</div>
              </div>
            </div>

            <!-- Pune -->
            <div class="contact-branch">
              <div>
                <div class="branch-name">Pune Branch</div>
                <div class="branch-address">Shop 10 & 11, Ground Floor, Elora Shopee, Indrayani Nagar, Bhosari, Pune</div>
              </div>
              <div class="branch-phone-list">
                <div class="branch-phone-item">📞 +91 80878 13700</div>
                <div class="branch-phone-item">📞 +91 96654 75432</div>
              </div>
            </div>

            <!-- Kolhapur -->
            <div class="contact-branch">
              <div>
                <div class="branch-name">Kolhapur Branch</div>
                <div class="branch-address">Shop No. 2, The Hub Apartment, 4th Lane, Shahupuri, Kolhapur</div>
              </div>
              <div class="branch-phone-list">
                <div class="branch-phone-item">📞 +91 88550 85858</div>
              </div>
            </div>

            <!-- Nanded -->
            <div class="contact-branch" style="margin-top: 5px;">
              <div>
                <div class="branch-name">Nanded Branch</div>
                <div class="branch-address">Near Bhagyanagar Corner, Opp. IIB Classes, Patnurkar Nagar, Nanded</div>
              </div>
              <div class="branch-phone-list">
                <div class="branch-phone-item">📞 +91 89830 01441</div>
                <div class="branch-phone-item">📞 +91 77988 09365</div>
              </div>
            </div>

            <!-- Solapur -->
            <div class="contact-branch" style="margin-top: 5px;">
              <div>
                <div class="branch-name">Solapur Branch</div>
                <div class="branch-address">Yogeshwar Net Cafe, 28 Nath Plaza, Railway Station Road, Solapur</div>
              </div>
              <div class="branch-phone-list">
                <div class="branch-phone-item">📞 +91 99237 54628</div>
              </div>
            </div>

          </div>
          
          <div class="counselling-callout">
            💬 To get more detailed counselling information, call us or WhatsApp us on any of the branch numbers listed above.
          </div>
        </div>

        <div class="print-footer">
          <div>Generated via Admissions Made Easy • Smart Predictor Engine</div>
          <div>Report Page 1</div>
        </div>

      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    w.document.write(printHTML);
    w.document.close();
    w.print();
  };

  /* ─── Bulk Process ─── */
  const handleBulkProcess = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      setBulkError("Please select an Excel file (.xlsx, .xls) to upload.");
      return;
    }
    setBulkError("");
    setBulkUploading(true);
    setBulkStatus("Uploading and parsing student Excel file...");
    setBulkStudents([]);
    setBulkExpandedIdx(null);

    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      
      const endpoint = stream === "STATE_QUOTA" ? "/medical-predictor/bulk-state-quota" : "/medical-predictor/bulk-aiims";

      const res = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const { total_students, students } = res.data;
      if (!students || students.length === 0) {
        setBulkError("No valid student rows found in the uploaded file.");
        setBulkUploading(false);
        return;
      }

      setBulkStudents(students);
      setBulkStatus(`Successfully processed ${total_students} students. Review results below and download PDFs.`);
    } catch (err) {
      console.error("Bulk upload error:", err);
      setBulkError(err.response?.data?.detail || "Failed to process bulk upload file.");
    } finally {
      setBulkUploading(false);
    }
  };

  /* ─── Download single student PDF (using window.open + print, same as single-person) ─── */
  const handleBulkStudentDownload = (s) => {
    const dateStr = new Date().toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const rowsHtml = [...(s.predictions || [])].sort((a, b) => a.cutoff_rank - b.cutoff_rank).map((p) => {
      const cc = CHANCE_COLORS[p.chance_label] || { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.1)", text: "#94a3b8" };
      const buffer = Math.round(p.student_rank - p.cutoff_rank);
      const bufferText = buffer <= 0 ? "-" + Math.abs(buffer).toLocaleString() : "+" + buffer.toLocaleString();
      const bufferColor = buffer <= 0 ? "#10b981" : "#f43f5e";
      const rowLeftBorder = "3px solid " + cc.text;

      return "<tr style=\"page-break-inside: avoid;\">" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; border-left: " + rowLeftBorder + "; font-weight: 600; color: #1e293b;\">" +
          "<div style=\"font-size: 11.5px; line-height: 1.3;\">" + p.college_name + "</div>" +
          "<div style=\"font-size: 9px; color: #64748b; margin-top: 2px;\">" + [p.college_code, p.status, p.state].filter(Boolean).join(" · ") + "</div>" +
        "</td>" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #334155; vertical-align: middle;\">" +
          "<div style=\"font-weight: 500;\">" + p.matched_category + "</div>" +
          "<div style=\"font-size: 9.5px; color: #64748b;\">" + p.matched_rank_type + "</div>" +
        "</td>" +
        (actuallyShowFees ? `<td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #10b981; font-weight: 600; vertical-align: middle;">${formatFees(p.fees)}</td>` : "") +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #475569; vertical-align: middle;\">" + Math.round(p.cutoff_rank).toLocaleString() + "</td>" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #1e293b; font-weight: 600; vertical-align: middle;\">" + Math.round(p.student_rank).toLocaleString() + "</td>" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: " + bufferColor + "; font-weight: 600; vertical-align: middle;\">" + bufferText + "</td>" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: " + cc.text + "; font-weight: 700; vertical-align: middle;\">" + (p.margin_percent != null ? (p.margin_percent > 0 ? "+" : "") + p.margin_percent.toFixed(1) + "%" : "--") + "</td>" +
        "<td style=\"padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: middle;\">" +
          "<span style=\"font-size: 9.5px; font-weight: 700; padding: 3px 8px; border-radius: 4px; background: " + cc.bg + "; border: 1px solid " + cc.border + "; color: " + cc.text + "; white-space: nowrap; display: inline-block;\">" + p.chance_label + "</span>" +
        "</td>" +
      "</tr>";
    }).join("");

    const summaryHtml = SUMMARY_KEYS.map(sk => {
      const val = s.summary?.[sk.key] || 0;
      if (val === 0) return "";
      return "<div style=\"display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 8px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; min-width: 75px; text-align: center;\">" +
        "<span style=\"font-size: 14px; font-weight: 800; color: " + sk.color + ";\">" + val + "</span>" +
        "<span style=\"font-size: 9px; font-weight: 600; color: #475569; margin-top: 1px;\">" + sk.label + "</span>" +
      "</div>";
    }).join("");

    const counsellorLine = currentAdmin?.name ? "<div style=\"font-size: 10px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;\">Counsellor: " + currentAdmin.name + "</div>" : "";

    const printHTML = "<!DOCTYPE html><html><head>" +
      "<title>Admissions Report - " + s.student_name + "</title>" +
      "<style>" +
        "@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');" +
        "body { font-family: 'Outfit', sans-serif; padding: 30px; color: #1e293b; background: #fff; margin: 0; line-height: 1.4; }" +
        ".brand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #38bdf8; padding-bottom: 15px; margin-bottom: 20px; }" +
        ".brand-title { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0; }" +
        ".brand-title span { color: #38bdf8; }" +
        ".brand-subtitle { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #64748b; font-weight: 700; margin-top: 2px; }" +
        ".doc-type-badge { background: #e0f2fe; color: #0369a1; padding: 4px 12px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; display: inline-block; }" +
        ".doc-date { font-size: 11px; color: #64748b; margin-top: 5px; font-weight: 500; }" +
        ".student-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 15px 20px; margin-bottom: 20px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }" +
        ".info-group { display: flex; flex-direction: column; }" +
        ".info-label { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: 700; margin-bottom: 3px; }" +
        ".info-value { font-size: 13px; color: #0f172a; font-weight: 600; }" +
        "table { width: 100%; border-collapse: collapse; font-size: 11.5px; margin-top: 10px; }" +
        "th { background: #0f172a; color: #fff; font-weight: 700; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.8px; padding: 10px; text-align: left; }" +
        "th:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }" +
        "th:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }" +
        ".watermark-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 480px; opacity: 0.045; pointer-events: none; z-index: -1; }" +
        ".watermark-container img { width: 100%; height: auto; }" +
        ".contact-section { margin-top: 25px; page-break-inside: avoid; border-top: 2px dashed #e2e8f0; padding-top: 15px; }" +
        ".contact-heading { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #475569; font-weight: 700; margin-bottom: 10px; }" +
        ".contact-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }" +
        ".contact-branch { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; display: flex; flex-direction: column; justify-content: space-between; }" +
        ".branch-name { font-size: 11px; font-weight: 800; color: #0f172a; margin-bottom: 5px; border-bottom: 1px solid #cbd5e1; padding-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px; }" +
        ".branch-address { font-size: 8.5px; color: #64748b; line-height: 1.3; margin-bottom: 6px; }" +
        ".branch-phone-item { font-size: 9.5px; font-weight: 600; color: #0284c7; display: flex; align-items: center; gap: 4px; }" +
        ".counselling-callout { margin-top: 12px; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 8px 12px; font-size: 10.5px; font-weight: 600; color: #0369a1; text-align: center; }" +
        ".print-footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 15px; display: flex; justify-content: space-between; font-size: 9.5px; color: #94a3b8; font-weight: 500; }" +
      "</style></head><body>" +
      "<div class=\"watermark-container\"><img src=\"/landing-assets/images/branding/ame_logo_watermark.png\" alt=\"AME Watermark\" /></div>" +
      "<div class=\"brand-header\"><div><h1 class=\"brand-title\">Admissions <span>Made Easy</span></h1><div class=\"brand-subtitle\">Smart College Admissions Predictor</div></div>" +
      "<div style=\"text-align: right;\"><div class=\"doc-type-badge\">NEET Prediction Report</div><div class=\"doc-date\">" + dateStr + "</div>" + counsellorLine + "</div></div>" +
      "<div class=\"student-card\">" +
        "<div class=\"info-group\"><div class=\"info-label\">Student Name</div><div class=\"info-value\">" + s.student_name + "</div></div>" +
        "<div class=\"info-group\"><div class=\"info-label\">Stream / Course</div><div class=\"info-value\">" + (stream === "STATE_QUOTA" ? "State Quota (Maharashtra 85%)" : stream === "AIQ_15" ? "AIQ 15% (All India)" : "AIIMS (All India)") + " · MBBS</div></div>" +
        "<div class=\"info-group\"><div class=\"info-label\">All India Rank (AIR)</div><div class=\"info-value\">" + s.air.toLocaleString() + "</div></div>" +
        "<div class=\"info-group\"><div class=\"info-label\">Reserved Category</div><div class=\"info-value\">" + s.category + (stream === "STATE_QUOTA" && s.gender ? ` (${s.gender})` : "") + "</div></div>" +
      "</div>" +
      "<div style=\"margin-bottom: 15px; padding: 10px 15px; background: #fef2f2; border: 1px solid #fca5a5; border-radius: 6px; font-size: 10px; color: #b91c1c; font-weight: 500;\">" +
      "🔴 <strong style=\"color: #991b1b;\">Note:</strong> All fee structures are based on the 2025–26 academic year. Fees for 2026–27 are subject to change. Please contact us for updated information." +
      "</div>" +
      "<div style=\"margin-bottom: 20px;\"><h3 style=\"font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #475569; font-weight: 700; margin-bottom: 8px;\">Prediction Analysis Summary</h3>" +
      "<div style=\"display: flex; gap: 8px; flex-wrap: wrap;\">" + summaryHtml + "</div></div>" +
      "<table><thead><tr>" +
        "<th style=\"width: 35%;\">College</th><th style=\"width: 12%;\">Category</th>" + (actuallyShowFees ? `<th style="width: 8%;">Fees</th>` : "") + "<th style=\"width: 10%;\">Cutoff</th>" +
        "<th style=\"width: 10%;\">Your Rank</th><th style=\"width: 11%;\">Buffer</th><th style=\"width: 10%;\">Margin</th><th style=\"width: 12%;\">Chance</th>" +
      "</tr></thead><tbody>" + rowsHtml + "</tbody></table>" +
      "<div class=\"contact-section\"><div class=\"contact-heading\">Our Admission Counselling Branches</div><div class=\"contact-grid\">" +
        "<div class=\"contact-branch\"><div><div class=\"branch-name\">Latur Branch (HQ)</div><div class=\"branch-address\">Ground Floor, Vishwa Travels Office, Near Udyog Bhavan, Shivaji Nagar, Latur</div></div><div class=\"branch-phone-item\">\uD83D\uDCDE +91 93095 53235 / +91 99708 09003</div></div>" +
        "<div class=\"contact-branch\"><div><div class=\"branch-name\">Pune Branch</div><div class=\"branch-address\">Shop 10 & 11, Ground Floor, Elora Shopee, Indrayani Nagar, Bhosari, Pune</div></div><div class=\"branch-phone-item\">\uD83D\uDCDE +91 80878 13700 / +91 96654 75432</div></div>" +
        "<div class=\"contact-branch\"><div><div class=\"branch-name\">Kolhapur Branch</div><div class=\"branch-address\">Shop No. 2, The Hub Apartment, 4th Lane, Shahupuri, Kolhapur</div></div><div class=\"branch-phone-item\">\uD83D\uDCDE +91 88550 85858</div></div>" +
        "<div class=\"contact-branch\" style=\"margin-top: 5px;\"><div><div class=\"branch-name\">Nanded Branch</div><div class=\"branch-address\">Near Bhagyanagar Corner, Opp. IIB Classes, Patnurkar Nagar, Nanded</div></div><div class=\"branch-phone-item\">\uD83D\uDCDE +91 89830 01441 / +91 77988 09365</div></div>" +
        "<div class=\"contact-branch\" style=\"margin-top: 5px;\"><div><div class=\"branch-name\">Solapur Branch</div><div class=\"branch-address\">Yogeshwar Net Cafe, 28 Nath Plaza, Railway Station Road, Solapur</div></div><div class=\"branch-phone-item\">\uD83D\uDCDE +91 99237 54628</div></div>" +
      "</div>" +
      "<div class=\"counselling-callout\">\uD83D\uDCAC To get more detailed counselling information, call us or WhatsApp us on any of the branch numbers listed above.</div></div>" +
      "<div class=\"print-footer\"><div>Generated via Admissions Made Easy \u2022 Smart Predictor Engine</div><div>Report Page 1</div></div>" +
      "</body></html>";

    return new Promise((resolve) => {
      let iframe = document.getElementById("bulk-print-iframe");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "bulk-print-iframe";
        iframe.style.visibility = "hidden";
        iframe.style.position = "absolute";
        iframe.style.width = "0px";
        iframe.style.height = "0px";
        iframe.style.border = "none";
        document.body.appendChild(iframe);
      }

      iframe.contentDocument.open();
      iframe.contentDocument.write(printHTML);
      iframe.contentDocument.close();

      // Wait a moment for fonts/images to load before printing
      setTimeout(() => {
        try {
          const originalTitle = document.title;
          document.title = `${s.air} - ${s.student_name}`;
          
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          document.title = originalTitle;
        } catch (e) {
          console.error("Print error:", e);
        }
        resolve();
      }, 800);
    });
  };

  /* ─── Download ALL bulk student PDFs sequentially ─── */
  const handleBulkDownloadAll = async () => {
    for (let i = 0; i < bulkStudents.length; i++) {
      await handleBulkStudentDownload(bulkStudents[i]);
      // Small pause between each print dialog
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  /* ─── CSS Keyframes (injected once) ─── */
  const keyframesId = "predictor-keyframes";
  if (typeof document !== "undefined" && !document.getElementById(keyframesId)) {
    const style = document.createElement("style");
    style.id = keyframesId;
    style.textContent = `
      @keyframes pred-pulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
      @keyframes pred-fadeIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      .pred-row:hover { background: rgba(255,255,255,0.04) !important; }
    `;
    document.head.appendChild(style);
  }

  // ── Compute which rounds have data — must stay above early returns (Rules of Hooks) ──
  const availableRounds = React.useMemo(() => {
    const rounds = [];
    if (!showRoundWiseCutoff || predictions.length === 0) return rounds;
    for (const rnd of ["R1", "R2", "R3", "R4"]) {
      const airKey = `${rnd.toLowerCase()}_air`;
      const scoreKey = `${rnd.toLowerCase()}_score`;
      const hasData = predictions.some(p => p[airKey] != null || p[scoreKey] != null);
      if (hasData) rounds.push(rnd);
    }
    return rounds;
  }, [showRoundWiseCutoff, predictions]);

  const roundCols = React.useMemo(() => {
    if (!showRoundWiseCutoff || availableRounds.length === 0) return [];
    if (selectedRound === "ALL") return availableRounds;
    return availableRounds.filter(r => r === selectedRound);
  }, [showRoundWiseCutoff, availableRounds, selectedRound]);

  if (loadingConfig) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#f39c12" }} />
      </div>
    );
  }

  if (!isPredictorEnabled && !isAdminPredictor) {
    return (
      <div className="space-y-6 h-full flex flex-col items-center justify-center py-10 md:py-20 relative">
        {/* Decorative */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 384, height: 384, background: 'rgba(243,156,18,0.05)', borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none' }} />

        <div className="max-w-md w-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, position: 'relative', zIndex: 1 }}>
          
          {/* Header graphic */}
          <div className="flex items-center justify-center relative overflow-hidden" style={{ height: 128, background: 'linear-gradient(135deg, rgba(243,156,18,0.8), rgba(211,84,0,0.8))' }}>
            <Sparkles className="absolute" style={{ top: 16, right: 16, color: 'rgba(255,255,255,0.3)', height: 32, width: 32 }} />
            <Target className="absolute" style={{ bottom: 16, left: 16, color: 'rgba(255,255,255,0.3)', height: 40, width: 40 }} />
            <div style={{ background: 'rgba(2,6,23,0.9)', padding: 12, borderRadius: '50%', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
              <Lock className="h-8 w-8" style={{ color: '#f39c12' }} />
            </div>
          </div>

          <div className="p-8 text-center space-y-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight mb-2" style={{ color: '#f8fafc' }}>AI College Predictor</h2>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" style={{ background: 'rgba(243,156,18,0.1)', color: '#f39c12' }}>
                Coming Soon
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#94a3b8' }}>
                Our AI-driven Predictor is currently analyzing the latest cutoff trends to give you <strong style={{ color: '#f8fafc' }}>99% accurate</strong> college admission predictions.
              </p>
            </div>

            <div className="p-4 rounded-xl text-left" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#f8fafc' }}>
                <BellRing className="h-4 w-4" style={{ color: '#f39c12' }} /> 
                Get Early Access
              </h4>
              <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                Update your profile score today, and we'll notify you the exact second this feature unlocks.
              </p>
              
              <div className="w-full h-10 rounded-xl flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.2)', color: '#f39c12' }}>
                ✓ You're on the priority waitlist
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getGridTemplate = () => {
    // All columns are fluid fr units — fills 100% width, no horizontal scroll
    const roundFr = roundCols.map(() => "1fr");
    return [
      "2.2fr",                          // College — widest
      colShowCategory ? "1fr" : "",    // Category
      actuallyShowFees ? "0.9fr" : "",      // Fees
      ...roundFr,                       // R1 / R2 / R3 / R4
      colShowCutoff   ? "0.8fr" : "",  // Cutoff — after rounds
      colShowYourRank ? "0.8fr" : "",  // Your Rank
      colShowBuffer   ? "0.8fr" : "",  // Buffer
      colShowMargin   ? "0.7fr" : "",  // Margin
      predictionScope === "ALL" ? "1.2fr" : "", // Exp. Round
      colShowChance   ? "1.1fr" : ""   // Chance
    ].filter(Boolean).join(" ");
  };

  return (
    <div style={{ width: "100%", margin: "0 auto", padding: "1rem 1rem 3rem", overflowX: "hidden" }}>

      {/* ── HERO HEADER ── */}
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "8px 20px", borderRadius: 30, background: "linear-gradient(135deg, rgba(243,156,18,0.15), rgba(211,84,0,0.1))", border: "1px solid rgba(243,156,18,0.25)", marginBottom: 12 }}>
          <Target size={18} style={{ color: "#f39c12" }} />
          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#f39c12", letterSpacing: "1.5px", textTransform: "uppercase" }}>NEET-UG College Predictor</span>
        </div>
        <h1 style={{ fontSize: "1.6rem", fontWeight: 800, color: "#f8fafc", margin: "0 0 6px", letterSpacing: "-0.5px" }}>
          Medical Admission Predictor
        </h1>
        <p style={{ fontSize: "0.8rem", color: "#94a3b8", maxWidth: 500, margin: "0 auto" }}>
          Enter your NEET rank details to predict your admission chances across colleges based on previous year cutoffs.
        </p>
      </div>

      {isAdminPredictor && (
        <div style={{ background: "rgba(15,23,42,0.4)", border: "1px solid rgba(243,156,18,0.25)", borderRadius: 16, padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setShowSettingsPanel(!showSettingsPanel)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Settings size={16} style={{ color: "#f39c12" }} />
              <h3 style={{ fontSize: "0.85rem", fontWeight: 700, color: "#f39c12", textTransform: "uppercase", letterSpacing: "0.5px", margin: 0 }}>
                Personalized Predictor Display Settings
              </h3>
            </div>
            <span style={{ fontSize: "0.72rem", color: "#f39c12", fontWeight: 600 }}>
              {showSettingsPanel ? "COLLAPSE" : "EXPAND"}
            </span>
          </div>

          {showSettingsPanel && (
            <div style={{ marginTop: "1.25rem" }}>
              
              {/* Streams Settings */}
              <div style={{ marginBottom: "1.25rem" }}>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>
                  Predictor Visibility & Stream Tabs
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                  {isSettingAllowed("isPredictorEnabled") && (
                    <PredictorSwitch 
                      label="Enable AI College Predictor" 
                      description="Turn on predictor tool visibility" 
                      checked={isPredictorEnabled} 
                      onChange={val => { setIsPredictorEnabled(val); updateAdminSetting("isPredictorEnabled", val); }} 
                    />
                  )}
                  {isSettingAllowed("allowedStreams_STATE_QUOTA") && (
                    <PredictorSwitch 
                      label="State Quota (Maharashtra 85%)" 
                      description="Display State Quota selection tab" 
                      checked={allowedStreams.STATE_QUOTA} 
                      onChange={val => {
                        const newStreams = { ...allowedStreams, STATE_QUOTA: val };
                        setAllowedStreams(newStreams);
                        updateAdminSetting("allowedStreams", newStreams);
                      }} 
                    />
                  )}
                  {isSettingAllowed("showSml") && (
                    <PredictorSwitch 
                      label="Display State Merit List (SML)" 
                      description="Collect SML rank input field" 
                      checked={showSml} 
                      onChange={val => { setShowSml(val); updateAdminSetting("showSml", val); }} 
                    />
                  )}
                  {isSettingAllowed("showCatRank") && (
                    <PredictorSwitch 
                      label="Display Category Rank" 
                      description="Collect Category Rank input field" 
                      checked={showCatRank} 
                      onChange={val => { setShowCatRank(val); updateAdminSetting("showCatRank", val); }} 
                    />
                  )}
                  {isSettingAllowed("allowedStreams_AIIMS") && (
                    <PredictorSwitch 
                      label="AIIMS (All India)" 
                      description="Display AIIMS selection tab" 
                      checked={allowedStreams.AIIMS} 
                      onChange={val => {
                        const newStreams = { ...allowedStreams, AIIMS: val };
                        setAllowedStreams(newStreams);
                        updateAdminSetting("allowedStreams", newStreams);
                      }} 
                    />
                  )}
                  {isSettingAllowed("allowedStreams_AIQ_15") && (
                    <PredictorSwitch 
                      label="AIQ 15% (All India)" 
                      description="Display AIQ 15% selection tab" 
                      checked={allowedStreams.AIQ_15} 
                      onChange={val => {
                        const newStreams = { ...allowedStreams, AIQ_15: val };
                        setAllowedStreams(newStreams);
                        updateAdminSetting("allowedStreams", newStreams);
                      }} 
                    />
                  )}
                  {isSettingAllowed("allowedStreams_DEEMED") && (
                    <PredictorSwitch 
                      label="Deemed Universities" 
                      description="Display Deemed selection tab" 
                      checked={allowedStreams.DEEMED} 
                      onChange={val => {
                        const newStreams = { ...allowedStreams, DEEMED: val };
                        setAllowedStreams(newStreams);
                        updateAdminSetting("allowedStreams", newStreams);
                      }} 
                    />
                  )}
                  {isSettingAllowed("allowedStreams_OTHER_STATES") && (
                    <PredictorSwitch 
                      label="Other States Quota" 
                      description="Display Other States selection tab" 
                      checked={allowedStreams.OTHER_STATES} 
                      onChange={val => {
                        const newStreams = { ...allowedStreams, OTHER_STATES: val };
                        setAllowedStreams(newStreams);
                        updateAdminSetting("allowedStreams", newStreams);
                      }} 
                    />
                  )}
                </div>
              </div>

              {/* Columns Settings */}
              <div>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>
                  Prediction Table Columns Display
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                  {isSettingAllowed("colShowCategory") && (
                    <PredictorSwitch 
                      label="Category Column" 
                      description="Display Category of cutoffs in table" 
                      checked={colShowCategory} 
                      onChange={val => { setColShowCategory(val); updateAdminSetting("colShowCategory", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowFees") && (
                    <PredictorSwitch 
                      label="Show Fees" 
                      description="Display the fees structure" 
                      checked={colShowFees} 
                      onChange={val => { setColShowFees(val); updateAdminSetting("colShowFees", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowCutoff") && (
                    <PredictorSwitch 
                      label="Cutoff Column" 
                      description="Display Cutoff ranks in table" 
                      checked={colShowCutoff} 
                      onChange={val => { setColShowCutoff(val); updateAdminSetting("colShowCutoff", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowYourRank") && (
                    <PredictorSwitch 
                      label="Your Rank Column" 
                      description="Display Student's rank in table" 
                      checked={colShowYourRank} 
                      onChange={val => { setColShowYourRank(val); updateAdminSetting("colShowYourRank", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowMargin") && (
                    <PredictorSwitch 
                      label="Margin Column" 
                      description="Display computed margins in table" 
                      checked={colShowMargin} 
                      onChange={val => { setColShowMargin(val); updateAdminSetting("colShowMargin", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowBuffer") && (
                    <PredictorSwitch 
                      label="Buffer Column" 
                      description="Display rank buffer in table" 
                      checked={colShowBuffer} 
                      onChange={val => { setColShowBuffer(val); updateAdminSetting("colShowBuffer", val); }} 
                    />
                  )}
                  {isSettingAllowed("allowPdfDownload") && (
                    <PredictorSwitch 
                      label="Allow PDF Download" 
                      description="Enable PDF download button" 
                      checked={allowPdfDownload} 
                      onChange={val => { setAllowPdfDownload(val); updateAdminSetting("allowPdfDownload", val); }} 
                    />
                  )}
                  {isSettingAllowed("colShowChance") && (
                    <PredictorSwitch 
                      label="Chance Column" 
                      description="Display Chance badge and row colors" 
                      checked={colShowChance} 
                      onChange={val => { setColShowChance(val); updateAdminSetting("colShowChance", val); }} 
                    />
                  )}
                </div>
              </div>

              {/* Round-Wise Cutoff Display */}
              <div>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>
                  Round-Wise Cutoff Display
                </div>
                {isSettingAllowed("showRoundWiseCutoff") && (
                  <PredictorSwitch
                    label="Display Round Wise Cutoff"
                    description="Show AIR [Score] cutoff broken down by round (R1–R4)"
                    checked={showRoundWiseCutoff}
                    onChange={val => setShowRoundWiseCutoff(val)}
                  />
                )}
                {isSettingAllowed("includeRoundsInPdf") && (
                  <PredictorSwitch
                    label="Include Rounds in PDF"
                    description="Include the selected round-wise cutoffs in the downloadable PDF report"
                    checked={includeRoundsInPdf}
                    onChange={val => setIncludeRoundsInPdf(val)}
                  />
                )}
                {showRoundWiseCutoff && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {["ALL", "R1", "R2", "R3", "R4"].map(r => (
                      <button
                        key={r}
                        onClick={() => setSelectedRound(r)}
                        style={{
                          padding: "5px 14px",
                          borderRadius: 999,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          border: selectedRound === r ? "1px solid rgba(56,189,248,0.6)" : "1px solid rgba(255,255,255,0.1)",
                          background: selectedRound === r ? "rgba(56,189,248,0.15)" : "rgba(255,255,255,0.03)",
                          color: selectedRound === r ? "#38bdf8" : "#94a3b8",
                          transition: "all 0.15s"
                        }}
                      >
                        {r === "ALL" ? "All Available Rounds" : `Only ${r}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      )}

      {/* ── FORM CARD ── */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "1.25rem", marginBottom: "1.25rem" }}>

        {/* Stream selector row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: "1.25rem" }}>
          {STREAM_OPTIONS.filter(opt => allowedStreams[opt.value]).map(opt => {
            const active = stream === opt.value;
            return (
              <button key={opt.value} onClick={() => handleStreamChange(opt.value)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: active ? "1px solid rgba(243,156,18,0.5)" : "1px solid rgba(255,255,255,0.08)", background: active ? "rgba(243,156,18,0.12)" : "rgba(255,255,255,0.03)", color: active ? "#f39c12" : "#94a3b8", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                {opt.icon}{opt.label}
              </button>
            );
          })}
        </div>

        {/* Controls grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>

          {/* Course */}
          <StyledSelect label="Course" icon={<GraduationCap size={12} style={{ color: "#a78bfa" }} />} value={course} onChange={e => setCourse(e.target.value)} id="pred-course">
            {(COURSE_MAP[stream] || []).map(c => <option key={c} value={c}>{c}</option>)}
          </StyledSelect>

          {/* State (only for OTHER_STATES and logged-in user) */}
          {stream === "OTHER_STATES" && (!!user || isAdminPredictor) && (
            <StyledSelect label="State" icon={<MapPin size={12} style={{ color: "#fb923c" }} />} value={state} onChange={e => setState(e.target.value)} id="pred-state">
              {STATE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </StyledSelect>
          )}

          {/* Category (State Quota + AIIMS + AIQ 15%) */}
          {needsCategory && (
            <StyledSelect label="Category" icon={<span style={{ fontSize: 12, color: "#34d399" }}>⬡</span>} value={category} onChange={e => setCategory(e.target.value)} id="pred-category">
              {((stream === "AIIMS" || stream === "AIQ_15") ? AIIMS_CATEGORIES : MAHA_CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
            </StyledSelect>
          )}

          {/* Gender (State Quota) */}
          {needsGender && (
            <StyledSelect label="Gender" icon={<span style={{ fontSize: 12, color: "#60a5fa" }}>♂♀</span>} value={gender} onChange={e => setGender(e.target.value)} id="pred-gender">
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </StyledSelect>
          )}
        </div>

        {/* ── Prediction Mode Selector (STATE_QUOTA only, logged in) ── */}
        {showModeSelector && (
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", marginBottom: 8 }}>Predict by</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {[
                { value: "SML",      label: "SML Only",           color: "#a78bfa", desc: "State Merit List" },
                { value: "CAT_RANK", label: "Category Rank Only",  color: "#34d399", desc: "Reserved Category Rank", hide: category === "OPEN" },
                { value: "ALL",      label: "Both (Best Match)",   color: "#38bdf8", desc: "Prioritizes Category Rank, falls back to SML" },
              ].filter(m => !m.hide).map(m => {
                const active = predictionMode === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => { setPredictionMode(m.value); setPredictions([]); setSummary(null); setHasSearched(false); setError(""); }}
                    title={m.desc}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      border: active ? `1px solid ${m.color}60` : "1px solid rgba(255,255,255,0.08)",
                      background: active ? `${m.color}18` : "rgba(255,255,255,0.03)",
                      color: active ? m.color : "#64748b",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 5
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: active ? m.color : "#334155", display: "inline-block", transition: "background 0.15s" }} />
                    {m.label}
                  </button>
                );
              })}
            </div>
            
            {/* ── Prediction Scope Selector ── */}
            <div style={{ marginTop: "1rem" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", marginBottom: 8 }}>Data Scope</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  { value: "ALL", label: "Overall (All Rounds)", color: "#38bdf8", desc: "Compares against all available rounds" },
                  { value: "R1",  label: "Round 1 Only",         color: "#f43f5e", desc: "Compares strictly against Round 1 cutoffs" },
                ].map(s => {
                  const active = predictionScope === s.value;
                  return (
                    <button
                      key={s.value}
                      onClick={() => { setPredictionScope(s.value); setPredictions([]); setSummary(null); setHasSearched(false); setError(""); }}
                      title={s.desc}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 999,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        border: active ? `1px solid ${s.color}60` : "1px solid rgba(255,255,255,0.08)",
                        background: active ? `${s.color}18` : "rgba(255,255,255,0.03)",
                        color: active ? s.color : "#64748b",
                        transition: "all 0.15s",
                        display: "flex",
                        alignItems: "center",
                        gap: 5
                      }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: active ? s.color : "#334155", display: "inline-block", transition: "background 0.15s" }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CTP Notice for EWS in State Quota */}
        {stream === "STATE_QUOTA" && category === "EWS" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.2)", marginBottom: "1rem", fontSize: "0.72rem", color: "#38bdf8" }}>
            <Sparkles size={14} style={{ flexShrink: 0 }} />
            <span><strong>CTP Protocol:</strong> Govt colleges are evaluated via <strong>EWS Category Rank</strong>. Private colleges (which have no EWS quota) are evaluated via <strong>SML Rank (OPEN Quota with 50% fees concession)</strong>.</span>
          </div>
        )}

        {/* Rank inputs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
          {needsAir && (
            <StyledInput label="All India Rank (AIR)" icon={<span style={{ fontSize: 11, color: "#f39c12" }}>#</span>} value={air} onChange={e => setAir(e.target.value)} placeholder="e.g. 50000" id="pred-air" required={predictionMode === "AIR" || stream !== "STATE_QUOTA"} />
          )}
          {needsSml && (
            <StyledInput 
              label={category === "EWS" && predictionMode === "CAT_RANK" ? "SML Rank (For Private Colleges)" : "State Merit List (SML)"} 
              icon={<span style={{ fontSize: 11, color: "#a78bfa" }}>#</span>} 
              value={sml} 
              onChange={e => setSml(e.target.value)} 
              placeholder={category === "EWS" && predictionMode === "CAT_RANK" ? "Enter SML for Private (OPEN)" : "e.g. 4000"} 
              id="pred-sml" 
              required={category === "EWS" && predictionMode === "CAT_RANK"}
            />
          )}
          {needsCatRank && (
            <StyledInput 
              label={category === "EWS" ? "EWS Category Rank (Govt Colleges)" : "Category Rank"} 
              icon={<span style={{ fontSize: 11, color: "#34d399" }}>#</span>} 
              value={catRank} 
              onChange={e => setCatRank(e.target.value)} 
              placeholder="e.g. 50" 
              id="pred-catrank" 
              required={predictionMode === "CAT_RANK" || predictionMode === "ALL"}
            />
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.25)", marginBottom: "1rem" }}>
            <AlertCircle size={16} style={{ color: "#f43f5e", flexShrink: 0 }} />
            <span style={{ fontSize: "0.78rem", color: "#fda4af" }}>{error}</span>
          </div>
        )}

        {/* Predict button */}
        <button onClick={handlePredict} disabled={loading} style={{ width: "100%", padding: "0.7rem", borderRadius: 10, border: "none", background: loading ? "rgba(243,156,18,0.3)" : "linear-gradient(135deg, #f39c12, #e67e22)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all 0.2s" }}>
          {loading ? <><Loader2 size={16} style={{ animation: "pred-pulse 1s infinite" }} /> Analyzing Cutoffs...</> : <><Target size={16} /> Predict Admission Chances</>}
        </button>

        {/* Bulk Upload Section (Only for AIIMS & State Quota Master Admin View) */}
        {(stream === "AIIMS" || stream === "STATE_QUOTA") && isMasterAdmin && (
          <div style={{ marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px dashed rgba(243,156,18,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <FileSpreadsheet size={18} style={{ color: "#f39c12" }} />
              <h3 style={{ fontSize: "0.85rem", fontWeight: 700, color: "#f39c12", textTransform: "uppercase", letterSpacing: "0.5px", margin: 0 }}>
                Bulk Upload {stream === "AIIMS" ? "AIIMS" : "State Quota"} Excel &amp; Generate Individual PDFs
              </h3>
            </div>
            <p style={{ fontSize: "0.72rem", color: "#94a3b8", margin: "0 0 12px" }}>
              Upload an Excel sheet containing columns: <strong>Student Name</strong>, <strong>CAT</strong> (Category), {stream === "STATE_QUOTA" && <span><strong>Gender</strong>, </span>}and <strong>ALL INDIA RANK</strong> (AIR). Review each student's report and download their PDFs individually.
            </p>

            <form onSubmit={handleBulkProcess} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input 
                  type="file" 
                  accept=".xlsx, .xls"
                  onChange={e => setBulkFile(e.target.files[0] || null)}
                  style={{
                    background: "rgba(0,0,0,0.4)",
                    padding: "0.5rem 0.7rem",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#f8fafc",
                    fontSize: "0.78rem",
                    flex: 1
                  }}
                />
                <button 
                  type="submit" 
                  disabled={bulkUploading || !bulkFile}
                  style={{
                    padding: "0.55rem 1.25rem",
                    borderRadius: 8,
                    border: "none",
                    background: bulkUploading || !bulkFile ? "rgba(243,156,18,0.3)" : "linear-gradient(135deg, #f39c12, #d35400)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    cursor: bulkUploading || !bulkFile ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  {bulkUploading ? <Loader2 size={14} style={{ animation: "pred-pulse 1s infinite" }} /> : <Upload size={14} />}
                  {bulkUploading ? "Processing..." : "Process Students"}
                </button>
              </div>

              {bulkStatus && (
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#38bdf8", background: "rgba(56,189,248,0.1)", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(56,189,248,0.2)" }}>
                  {bulkStatus}
                </div>
              )}

              {bulkError && (
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#f43f5e", background: "rgba(244,63,94,0.1)", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(244,63,94,0.2)" }}>
                  {bulkError}
                </div>
              )}
            </form>
          </div>
        )}
      </div>

      {/* ── BULK RESULTS SECTION ── */}
      {bulkStudents.length > 0 && isMasterAdmin && (
        <div style={{ animation: "pred-fadeIn 0.4s ease-out", marginTop: "1rem" }}>
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FileSpreadsheet size={18} style={{ color: "#f39c12" }} />
                <span style={{ fontSize: "0.82rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#f8fafc" }}>
                  Bulk Student Results ({bulkStudents.length} Students)
                </span>
              </div>
              <button
                onClick={handleBulkDownloadAll}
                style={{
                  padding: "0.45rem 1rem",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                <Download size={14} /> Download All PDFs
              </button>
            </div>

            {bulkStudents.map((s, idx) => {
              const isExpanded = bulkExpandedIdx === idx;
              const totalPreds = s.predictions?.length || 0;
              const safeCount = s.summary?.almost_certain || 0;
              const highCount = s.summary?.very_likely || 0;

              return (
                <div key={idx} style={{ marginBottom: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden", background: "rgba(0,0,0,0.2)" }}>
                  {/* Student Row Header */}
                  <div
                    onClick={() => setBulkExpandedIdx(isExpanded ? null : idx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.65rem 1rem",
                      cursor: "pointer",
                      transition: "background 0.2s",
                      background: isExpanded ? "rgba(243,156,18,0.08)" : "transparent"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "#f39c12", minWidth: 28 }}>#{idx + 1}</span>
                      <div>
                        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#f8fafc" }}>{s.student_name}</div>
                        <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 2 }}>
                          AIR: {s.air.toLocaleString()} · Category: {s.category} · {totalPreds} colleges analyzed
                          {safeCount > 0 && <span style={{ color: "#38bdf8", marginLeft: 6 }}>({safeCount} Safe)</span>}
                          {highCount > 0 && <span style={{ color: "#10b981", marginLeft: 4 }}>({highCount} High Chance)</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleBulkStudentDownload(s); }}
                        style={{
                          padding: "0.35rem 0.75rem",
                          borderRadius: 6,
                          border: "1px solid rgba(56,189,248,0.3)",
                          background: "rgba(56,189,248,0.1)",
                          color: "#38bdf8",
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}
                      >
                        <Download size={12} /> PDF
                      </button>
                      <ChevronDown size={16} style={{ color: "#94a3b8", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                    </div>
                  </div>

                  {/* Expanded Predictions Table */}
                  {isExpanded && (
                    <div style={{ padding: "0 1rem 0.75rem 1rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      {/* Summary badges */}
                      {s.summary && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "10px 0" }}>
                          {SUMMARY_KEYS.map(sk => {
                            const val = s.summary?.[sk.key] || 0;
                            if (val === 0) return null;
                            return (
                              <div key={sk.key} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                <span style={{ fontSize: "0.78rem", fontWeight: 800, color: sk.color }}>{val}</span>
                                <span style={{ fontSize: "0.62rem", fontWeight: 600, color: "#94a3b8" }}>{sk.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Prediction rows */}
                      <div style={{ maxHeight: 320, overflowY: "auto", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.72rem" }}>
                          <thead>
                            <tr style={{ background: "rgba(15,23,42,0.8)", position: "sticky", top: 0, zIndex: 1 }}>
                              <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>College</th>
                              <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Category</th>
                              <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Cutoff</th>
                              <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Your Rank</th>
                              <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Chance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...(s.predictions || [])].sort((a, b) => a.cutoff_rank - b.cutoff_rank).map((p, pIdx) => {
                              const cc = CHANCE_COLORS[p.chance_label] || CHANCE_COLORS["Dream College"];
                              return (
                                <tr key={pIdx} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                                  <td style={{ padding: "5px 8px", color: "#f8fafc", fontWeight: 600 }}>
                                    <div style={{ fontSize: "0.72rem" }}>{p.college_name}</div>
                                    <div style={{ fontSize: "0.6rem", color: "#64748b" }}>{p.state}</div>
                                  </td>
                                  <td style={{ padding: "5px 8px", color: "#cbd5e1", fontSize: "0.68rem" }}>{p.matched_category}</td>
                                  <td style={{ padding: "5px 8px", color: "#cbd5e1", fontSize: "0.68rem" }}>{Math.round(p.cutoff_rank).toLocaleString()}</td>
                                  <td style={{ padding: "5px 8px", color: "#f8fafc", fontWeight: 600, fontSize: "0.68rem" }}>{Math.round(p.student_rank).toLocaleString()}</td>
                                  <td style={{ padding: "5px 8px" }}>
                                    <span style={{ fontSize: "0.6rem", fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text, whiteSpace: "nowrap" }}>
                                      {p.chance_label}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── RESULTS SECTION ── */}
      {hasSearched && !loading && (
        <div style={{ animation: "pred-fadeIn 0.4s ease-out" }}>

          {/* Note Banner */}
          <div style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: 8, padding: "10px 16px", marginBottom: "1rem", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: "1rem" }}>🔴</span>
            <span style={{ fontSize: "0.78rem", color: "#fca5a5", fontWeight: 500, lineHeight: 1.4 }}>
              <strong style={{ color: "#ef4444" }}>Note:</strong> All fee structures are based on the 2025–26 academic year. Fees for 2026–27 are subject to change. Please contact us for updated information.
            </span>
          </div>

          {/* Summary Banner */}
          {summary && summary.total > 0 && (
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "1rem 1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#94a3b8" }}>Prediction Summary</span>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f39c12" }}>{summary.total} Colleges Analyzed</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {SUMMARY_KEYS.map(sk => {
                  const count = summary[sk.key] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={sk.key} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 8, background: `${sk.color}15`, border: `1px solid ${sk.color}30` }}>
                      <span style={{ fontSize: "0.8rem", fontWeight: 800, color: sk.color }}>{count}</span>
                      <span style={{ fontSize: "0.62rem", fontWeight: 600, color: sk.color, opacity: 0.85 }}>{sk.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search + count bar */}
          {predictions.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "0.75rem", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: isGuest ? "#475569" : "#64748b" }} />
                <input
                  value={isGuest ? "" : searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  disabled={isGuest}
                  placeholder={isGuest ? "Sign in or create profile to filter predictions..." : "Filter by college name, state, or code..."}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 32px",
                    borderRadius: 8,
                    border: isGuest ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(255,255,255,0.08)",
                    background: isGuest ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.3)",
                    color: isGuest ? "#64748b" : "#f8fafc",
                    fontSize: "0.78rem",
                    outline: "none",
                    fontFamily: "inherit",
                    cursor: isGuest ? "not-allowed" : "text"
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: "0.7rem", color: "#64748b", whiteSpace: "nowrap" }}>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
                {((allowPdfDownload && !isGuest) || isAdminPredictor) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isAdminPredictor && (
                      <input
                        type="text"
                        placeholder="Student Name for PDF..."
                        value={pdfStudentName}
                        onChange={e => setPdfStudentName(e.target.value)}
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "#f8fafc",
                          padding: "6px 10px",
                          borderRadius: 6,
                          fontSize: "0.72rem",
                          outline: "none",
                          width: "160px",
                          fontFamily: "inherit"
                        }}
                      />
                    )}
                    <button
                      onClick={handleDownloadPDF}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(56,189,248,0.1)",
                        border: "1px solid rgba(56,189,248,0.25)",
                        color: "#38bdf8",
                        padding: "6px 12px",
                        borderRadius: 6,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        fontFamily: "inherit",
                        whiteSpace: "nowrap"
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(56,189,248,0.18)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(56,189,248,0.1)"; }}
                    >
                      <Download size={12} />
                      Download PDF
                    </button>
                  </div>
                )}

                {isAdminPredictor && predictions.length > 0 && (
                  <button
                    onClick={() => {
                      setSuggAir(air || "");
                      setSuggSml(sml || "");
                      setSuggCatRank(catRank || "");
                      setShowSuggestionModal(true);
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "linear-gradient(135deg, rgba(243,156,18,0.18), rgba(129,140,248,0.18))",
                      border: "1px solid rgba(243,156,18,0.45)",
                      color: "#f39c12", padding: "6px 14px", borderRadius: 6,
                      fontSize: "0.72rem", fontWeight: 800, cursor: "pointer",
                      fontFamily: "inherit", whiteSpace: "nowrap"
                    }}
                  >
                    ✨ Start Suggestion List
                  </button>
                )}
              </div>
            </div>
          )}
          {filtered.length > 0 ? (
            <div style={{ position: "relative", width: "100%" }}>
              {/* Table — no horizontal scroll, fluid columns */}
              <div style={{ width: "100%", borderRadius: 14 }}>
                <div style={{ width: "100%", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
                  {/* table header */}
                  <div style={{ display: "grid", gridTemplateColumns: getGridTemplate(), gap: 0, padding: "8px 12px", background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)", borderLeft: "3.5px solid transparent" }}>
                    {[
                      { name: "College", show: true },
                      { name: "Cat.", show: colShowCategory },
                      { name: "Fees", show: actuallyShowFees },
                      ...roundCols.map(r => ({ name: r, show: true, isRound: true })),
                      { name: "Cutoff", show: colShowCutoff },
                      { name: "Rank", show: colShowYourRank },
                      { name: "Buffer", show: colShowBuffer },
                      { name: "Margin", show: colShowMargin },
                      { name: "Exp. Round", show: predictionScope === "ALL" },
                      { name: "Chance", show: colShowChance }
                    ].filter(h => h.show).map(h => (
                      <span key={h.name} style={{ fontSize: "0.58rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: h.isRound ? "#38bdf8" : "#64748b" }}>{h.name}</span>
                    ))}
                  </div>
                  {/* table rows */}
                  <div style={{ maxHeight: 520, overflowY: "auto" }}>
                    {filtered.slice(0, isGuest ? 3 : undefined).map((p, i) => {
                      const cc = CHANCE_COLORS[p.chance_label] || CHANCE_COLORS["Dream College"];
                      const buffer = Math.round(p.student_rank - p.cutoff_rank);
                      const rowLeftBorder = colShowChance ? `3.5px solid ${cc.text}` : "3.5px solid transparent";
                      const rowBg = colShowChance ? `linear-gradient(90deg, ${cc.bg} 0%, rgba(255, 255, 255, 0.01) 60%, transparent 100%)` : "transparent";

                      return (
                        <div key={i} className="pred-row" style={{ display: "grid", gridTemplateColumns: getGridTemplate(), gap: 0, padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)", borderLeft: rowLeftBorder, background: rowBg, transition: "all 0.15s", cursor: "default" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e2e8f0", whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.3" }} title={p.college_name}>{p.college_name}</span>
                            <span style={{ fontSize: "0.58rem", color: "#64748b" }}>
                              {[p.college_code, p.status, p.state].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                          {colShowCategory && (
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                              <span style={{ fontSize: "0.65rem", color: "#cbd5e1" }}>{p.matched_category}</span>
                              <span style={{ fontSize: "0.55rem", color: "#64748b" }}>{p.matched_rank_type}</span>
                            </div>
                          )}
                          {actuallyShowFees && (
                            <span style={{ fontSize: "0.68rem", color: "#10b981", display: "flex", alignItems: "center", fontWeight: 600 }}>{formatFees(p.fees)}</span>
                          )}
                          {/* ── Per-round columns ── */}
                          {roundCols.map(rnd => {
                            const airVal = p[`${rnd.toLowerCase()}_air`];
                            const scoreVal = p[`${rnd.toLowerCase()}_score`];
                            const smlVal = p[`${rnd.toLowerCase()}_sml`];
                            const catVal = p[`${rnd.toLowerCase()}_cat_rank`];

                            const showAir = predictionMode === "AIR" || predictionMode === "ALL" || stream !== "STATE_QUOTA";
                            const showSml = stream === "STATE_QUOTA" && (predictionMode === "SML" || predictionMode === "ALL");
                            const showCat = stream === "STATE_QUOTA" && (predictionMode === "CAT_RANK" || predictionMode === "ALL");
                            
                            const hasAny = (showAir && (airVal != null || scoreVal != null)) || (showSml && smlVal != null) || (showCat && catVal != null);

                            return (
                              <div key={rnd} style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6, padding: "2px 0" }}>
                                {hasAny ? (
                                  <>
                                    {showAir && airVal != null && (
                                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <span style={{ fontSize: "0.68rem", color: "#38bdf8", fontWeight: 600 }}>{Math.round(airVal).toLocaleString()}</span>
                                        {predictionMode === "ALL" && <span style={{ fontSize: "0.5rem", color: "#475569", textTransform: "uppercase" }}>AIR</span>}
                                        {scoreVal != null && <span style={{ fontSize: "0.55rem", color: "#64748b", marginTop: -1 }}>{Math.round(scoreVal)}</span>}
                                      </div>
                                    )}
                                    {showSml && smlVal != null && (
                                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <span style={{ fontSize: "0.68rem", color: "#a78bfa", fontWeight: 600 }}>{Math.round(smlVal).toLocaleString()}</span>
                                        {predictionMode === "ALL" && <span style={{ fontSize: "0.5rem", color: "#475569", textTransform: "uppercase" }}>SML</span>}
                                      </div>
                                    )}
                                    {showCat && catVal != null && (
                                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <span style={{ fontSize: "0.68rem", color: "#34d399", fontWeight: 600 }}>{Math.round(catVal).toLocaleString()}</span>
                                        {predictionMode === "ALL" && <span style={{ fontSize: "0.5rem", color: "#475569", textTransform: "uppercase" }}>CAT-RANK</span>}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ fontSize: "0.6rem", color: "#334155" }}>—</span>
                                )}
                              </div>
                            );
                          })}
                          {colShowCutoff && (
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                              <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600 }}>{Math.round(p.cutoff_rank).toLocaleString()}</span>
                              <span style={{ fontSize: "0.52rem", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>{p.matched_rank_type}</span>
                            </div>
                          )}
                          {colShowYourRank && (
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                              <span style={{ fontSize: "0.72rem", color: "#e2e8f0", fontWeight: 600 }}>{Math.round(p.student_rank).toLocaleString()}</span>
                              <span style={{ fontSize: "0.52rem", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>{p.matched_rank_type}</span>
                            </div>
                          )}
                          {colShowBuffer && (
                            <span style={{ fontSize: "0.72rem", fontWeight: 600, color: buffer <= 0 ? "#10b981" : "#f43f5e", display: "flex", alignItems: "center" }}>
                              {buffer <= 0 ? "-" : "+"}{Math.abs(buffer).toLocaleString()}
                            </span>
                          )}
                          {colShowMargin && (
                            <span style={{ fontSize: "0.7rem", fontWeight: 700, color: cc.text, display: "flex", alignItems: "center" }}>{p.margin_percent != null ? (p.margin_percent > 0 ? "+" : "") + p.margin_percent.toFixed(1) + "%" : "--"}</span>
                          )}
                          {predictionScope === "ALL" && (
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                              {p.expected_rounds && p.expected_rounds.length > 0 ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {p.expected_rounds.map((er, idx) => {
                                      const errCc = CHANCE_COLORS[er.chance] || CHANCE_COLORS["Dream College"];
                                      return (
                                          <React.Fragment key={idx}>
                                              {idx > 0 && <span style={{color: "#475569", fontSize: "0.5rem", marginTop: 2}}>➔</span>}
                                              <span 
                                                title={(er.round === "R4" || er.round === "R3") ? "Stray Vacancy: Seats may not remain vacant. Check the seat matrix and discuss with your counsellor. Only eligible if no prior seat was allotted." : ""}
                                                style={{ fontSize: "0.55rem", padding: "1px 4px", borderRadius: 4, background: errCc.bg, border: `1px solid ${errCc.border}`, color: errCc.text, whiteSpace: "nowrap" }}
                                              >
                                                {er.round} ({er.chance === "Safe Zone" ? "Safe" : er.chance.replace(" Chance", "").replace("Choice", "Ch.")})
                                              </span>
                                          </React.Fragment>
                                      );
                                  })}
                                </div>
                              ) : (
                                <span style={{ fontSize: "0.6rem", color: "#334155" }}>—</span>
                              )}
                            </div>
                          )}
                          {colShowChance && (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span style={{ fontSize: "0.6rem", fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text, whiteSpace: "nowrap" }}>
                                {p.chance_label}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Blurred mock rows for guest preview */}
                    {isGuest && filtered.length > 3 && (
                      <div style={{ filter: "blur(6px)", opacity: 0.35, pointerEvents: "none", userSelect: "none" }}>
                        {filtered.slice(3, 6).map((p, i) => {
                          const cc = CHANCE_COLORS[p.chance_label] || CHANCE_COLORS["Dream College"];
                          const rowLeftBorder = colShowChance ? `3.5px solid ${cc.text}` : "3.5px solid transparent";
                          const rowBg = colShowChance ? `linear-gradient(90deg, ${cc.bg} 0%, rgba(255, 255, 255, 0.01) 60%, transparent 100%)` : "transparent";

                          return (
                            <div key={`blur-${i}`} style={{ display: "grid", gridTemplateColumns: getGridTemplate(), gap: 0, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", borderLeft: rowLeftBorder, background: rowBg }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#cbd5e1", whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.25" }}>{p.college_name}</span>
                                <span style={{ fontSize: "0.6rem", color: "#64748b" }}>{p.college_code}</span>
                              </div>
                              {colShowCategory && (
                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                  <span style={{ fontSize: "0.72rem", color: "#cbd5e1" }}>{p.matched_category}</span>
                                </div>
                              )}
                              {colShowCutoff && (
                                <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>12,345</span>
                              )}
                              {colShowYourRank && (
                                <span style={{ fontSize: "0.78rem", color: "#e2e8f0" }}>23,456</span>
                              )}
                              {colShowBuffer && (
                                <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>-1,500</span>
                              )}
                              {colShowMargin && (
                                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: cc.text }}>+10.5%</span>
                              )}
                              {colShowChance && (
                                <div>
                                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: cc.bg, color: cc.text }}>
                                    {p.chance_label}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Floating CTA Overlay (outside table scroll layout) */}
              {isGuest && filtered.length > 3 && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", background: "linear-gradient(to bottom, transparent 15%, rgba(15, 23, 42, 0.95) 60%)", padding: "1rem", textAlign: "center", borderRadius: 14, zIndex: 10 }}>
                  <div style={{ background: "rgba(30, 41, 59, 0.92)", border: "1px solid rgba(245, 158, 11, 0.25)", borderRadius: 20, padding: "1.5rem 1.25rem", width: "95%", maxWidth: 360, backdropFilter: "blur(12px)", boxShadow: "0 10px 30px rgba(0, 0, 0, 0.65)", marginBottom: "0.5rem" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "50%", background: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.2)", marginBottom: 12 }}>
                      <Lock size={18} style={{ color: "#f59e0b" }} />
                    </div>
                    <h4 style={{ fontSize: "0.95rem", fontWeight: 800, color: "#f8fafc", margin: "0 0 6px" }}>Unlock {filtered.length - 3}+ More College Predictions</h4>
                    <p style={{ fontSize: "0.72rem", color: "#94a3b8", lineHeight: "1.45", margin: "0 0 16px" }}>
                      Create a free student profile or sign in to view complete listings, analyses, and secure your admission guidance.
                    </p>
                    <button onClick={handleOpenAuth} style={{ width: "100%", padding: "11px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: "0 4px 12px rgba(245, 158, 11, 0.35)", transition: "all 0.2s" }}>
                      <Sparkles size={14} /> Unlock All Predictions Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            predictions.length === 0 && !error && (
              <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#64748b" }}>
                <Target size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <p style={{ fontSize: "0.85rem", fontWeight: 600, color: "#94a3b8" }}>No predictions found</p>
                <p style={{ fontSize: "0.75rem" }}>Try adjusting your rank inputs or changing the stream/course selection.</p>
              </div>
            )
          )}
        </div>
      )}

      <TeaserAuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

      {/* ── Start Suggestion List Modal ── */}
      {showSuggestionModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "28px 28px 24px", width: "100%", maxWidth: 480, boxShadow: "0 25px 80px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#f8fafc", marginBottom: 6 }}>✨ Start Suggestion List</div>
            <div style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 20 }}>Create a student profile to save and track the counselling priority list.</div>

            {/* Reg Number */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>Registration Number <span style={{ color: "#ef4444" }}>*</span></label>
              <input value={suggRegNumber} onChange={e => setSuggRegNumber(e.target.value)} placeholder="e.g. MH2025-12345" style={{ width: "100%", padding: "9px 12px", background: "rgba(255,255,255,0.05)", border: suggRegNumber ? "1px solid rgba(56,189,248,0.4)" : "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#f8fafc", fontSize: "0.82rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>

            {/* Student Name */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>Student Name <span style={{ color: "#475569" }}>(optional)</span></label>
              <input value={suggStudentName} onChange={e => setSuggStudentName(e.target.value)} placeholder="e.g. Rahul Sharma" style={{ width: "100%", padding: "9px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#f8fafc", fontSize: "0.82rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>

            {/* Ranks Row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 4 }}>AIR <span style={{ color: "#475569" }}>(opt)</span></label>
                <input value={suggAir} onChange={e => setSuggAir(e.target.value)} placeholder="e.g. 42000" style={{ width: "100%", padding: "7px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, color: "#38bdf8", fontSize: "0.8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 4 }}>SML <span style={{ color: "#475569" }}>(opt)</span></label>
                <input value={suggSml} onChange={e => setSuggSml(e.target.value)} placeholder="e.g. 5765" style={{ width: "100%", padding: "7px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, color: "#a78bfa", fontSize: "0.8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Cat. Rank <span style={{ color: "#475569" }}>(opt)</span></label>
                <input value={suggCatRank} onChange={e => setSuggCatRank(e.target.value)} placeholder="e.g. 1200" style={{ width: "100%", padding: "7px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, color: "#34d399", fontSize: "0.8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Auto-filled info */}
            <div style={{ padding: "10px 14px", background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 8, marginBottom: 20, fontSize: "0.68rem", color: "#64748b", display: "flex", flexWrap: "wrap", gap: 10 }}>
              <span>Category: <strong style={{ color: "#f8fafc" }}>{category || "—"}</strong></span>
              <span>Gender: <strong style={{ color: "#f8fafc" }}>{gender || "—"}</strong></span>
              <span>Course: <strong style={{ color: "#f8fafc" }}>{course || "—"}</strong></span>
              <span>Stream: <strong style={{ color: "#f8fafc" }}>{stream || "—"}</strong></span>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowSuggestionModal(false)} style={{ padding: "8px 18px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", borderRadius: 8, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button
                disabled={!suggRegNumber.trim() || suggModalLoading}
                onClick={async () => {
                  if (!suggRegNumber.trim()) { toast.error("Registration number is required"); return; }
                  setSuggModalLoading(true);
                  try {
                    const payload = {
                      reg_number: suggRegNumber.trim(),
                      student_name: suggStudentName.trim(),
                      air: suggAir ? parseInt(suggAir, 10) : null,
                      sml: suggSml ? parseInt(suggSml, 10) : null,
                      category_rank: suggCatRank ? parseInt(suggCatRank, 10) : null,
                      category: category || "",
                      gender: gender || "",
                      course: course || "",
                      stream: stream || "",
                      state: state || "",
                      prediction_scope: predictionScope || "ALL",
                      prediction_mode: predictionMode || "ALL",
                      prediction_data: predictions || []
                    };
                    const res = await api.post("/public-admin/medical-students", payload, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Student profile created!");
                    setShowSuggestionModal(false);
                    setSuggRegNumber(""); setSuggStudentName("");
                    navigate(`/portal-admin/medical-counsellor/${res.data.id}`, { state: { prediction_data: predictions } });
                  } catch (err) {
                    console.error("Profile creation error:", err);
                    console.error("Response data:", err?.response?.data);
                    const errorMsg = err?.response?.data?.detail || err.message || "Failed to create student profile";
                    toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
                  } finally { setSuggModalLoading(false); }
                }}
                style={{ padding: "8px 22px", background: suggRegNumber.trim() ? "linear-gradient(135deg, #f39c12, #818cf8)" : "rgba(255,255,255,0.05)", border: "none", color: suggRegNumber.trim() ? "#fff" : "#475569", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, cursor: suggRegNumber.trim() ? "pointer" : "default", fontFamily: "inherit" }}
              >
                {suggModalLoading ? "Creating…" : "Create & Open Workspace →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
